import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { checkRateLimit } from '../utils/rateLimiter';

export const useProfileViews = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);

    // Track a profile view
    const trackProfileView = async (viewedProfileId, context = {}) => {
        if (!user || !viewedProfileId) return;
        
        // Don't track self-views
        if (user.id === viewedProfileId) {
            return;
        }

        // Rate limit: Max 100 profile views per hour per user
        const rateLimitKey = `profile_view_${user.id}`;
        if (!checkRateLimit(rateLimitKey, 100, 3600000)) {
            // Silently fail - don't spam profile views
            return;
        }

        try {
            setLoading(true);
            
            const viewData = {
                viewer_id: user.id,
                viewed_profile_id: viewedProfileId,
                viewer_role: user.user_metadata?.role || 'Unknown',
                viewed_profile_role: context.profileRole || 'Unknown',
                view_context: context.source || 'unknown', // e.g., 'candidate_search', 'company_search', 'direct_link'
                view_timestamp: new Date().toISOString(),
                ip_address: context.ipAddress || null,
                user_agent: context.userAgent || null,
                referrer: context.referrer || null
            };

            const { error } = await supabase
                .from('profile_views')
                .insert([viewData]);

            if (error) {
                // Silent fail for profile view tracking
            }
        } catch (error) {
            // Silent fail for profile view tracking
        } finally {
            setLoading(false);
        }
    };

    // Get profile views for a specific profile
    const getProfileViews = async (profileId, limit = 50) => {
        if (!profileId) return [];

        try {
            const { data, error } = await supabase
                .from('profile_views')
                .select(`
                    *,
                    viewer:viewer_id (
                        id,
                        email,
                        user_metadata
                    )
                `)
                .eq('viewed_profile_id', profileId)
                .order('view_timestamp', { ascending: false })
                .limit(limit);

            if (error) {
                return [];
            }

            return data || [];
        } catch (error) {
            return [];
        }
    };

    // Get views by a specific user (who they've viewed)
    const getViewsByUser = async (userId, limit = 50) => {
        if (!userId) return [];

        try {
            const { data, error } = await supabase
                .from('profile_views')
                .select(`
                    *,
                    viewed_profile:viewed_profile_id (
                        id,
                        email,
                        user_metadata
                    )
                `)
                .eq('viewer_id', userId)
                .order('view_timestamp', { ascending: false })
                .limit(limit);

            if (error) {
                return [];
            }

            return data || [];
        } catch (error) {
            return [];
        }
    };

    // Get profile view statistics
    const getProfileViewStats = async (profileId) => {
        // Validate profileId - must be a non-empty string
        if (!profileId || typeof profileId !== 'string' || profileId.trim() === '') {
            console.warn('getProfileViewStats: Invalid profileId', profileId);
            return {
                totalViews: 0,
                uniqueViewers: 0,
                viewsByRole: {},
                viewsByDay: {},
                recentViews: []
            };
        }

        try {
            const { data, error } = await supabase
                .from('profile_views')
                .select('viewer_id, viewer_role, view_timestamp, viewed_profile_id')
                .eq('viewed_profile_id', profileId)
                .order('view_timestamp', { ascending: false });

            if (error) {
                console.error('getProfileViewStats: Database error', error, 'profileId:', profileId);
                return {
                    totalViews: 0,
                    uniqueViewers: 0,
                    viewsByRole: {},
                    viewsByDay: {},
                    recentViews: []
                };
            }

            if (!data || data.length === 0) {
                return {
                    totalViews: 0,
                    uniqueViewers: 0,
                    viewsByRole: {},
                    viewsByDay: {},
                    recentViews: []
                };
            }

            // Verify that all returned views are for the correct profile
            const invalidViews = data.filter(view => {
                // Verify data structure and that viewed_profile_id matches
                return !view.view_timestamp || !view.viewer_id || view.viewed_profile_id !== profileId;
            });

            if (invalidViews.length > 0) {
                console.error('getProfileViewStats: Found invalid views that do not match profileId!', {
                    invalidCount: invalidViews.length,
                    requestedProfileId: profileId,
                    totalReturned: data.length
                });
            }

            // Filter to only include valid views for this profile
            const validData = data.filter(view => 
                view.view_timestamp && 
                view.viewer_id && 
                view.viewed_profile_id === profileId
            );

            if (validData.length !== data.length) {
                console.warn('getProfileViewStats: Filtered out invalid views', {
                    originalCount: data.length,
                    validCount: validData.length,
                    profileId
                });
            }

            // Use only valid data for stats calculation
            const statsData = validData;

            const stats = {
                totalViews: statsData.length,
                uniqueViewers: new Set(statsData.map(view => view.viewer_id)).size,
                viewsByRole: {},
                viewsByDay: {},
                recentViews: statsData // Return all views, sorted by most recent first
            };

            // Group by role
            statsData.forEach(view => {
                const role = view.viewer_role || 'Unknown';
                stats.viewsByRole[role] = (stats.viewsByRole[role] || 0) + 1;
            });

            // Group by day
            statsData.forEach(view => {
                const day = new Date(view.view_timestamp).toDateString();
                stats.viewsByDay[day] = (stats.viewsByDay[day] || 0) + 1;
            });

            return stats;
        } catch (error) {
            console.error('getProfileViewStats: Exception', error, 'profileId:', profileId);
            return {
                totalViews: 0,
                uniqueViewers: 0,
                viewsByRole: {},
                viewsByDay: {},
                recentViews: []
            };
        }
    };

    return {
        trackProfileView,
        getProfileViews,
        getViewsByUser,
        getProfileViewStats,
        loading
    };
};

export default useProfileViews;
